import React, { useEffect, useState, useRef } from 'react';
import { DayTabs, TimeSlot, SessionModalWithContext as SessionModal } from '@portal/components/event_common';
import { ExpandedSessionCardV1 } from './ExpandedSessionCardV1';
import { PortalModal } from '../../../components/PortalModal';
import { Search, Download, Calendar, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { ScheduleLoadingState } from '../../../components/schedule/ScheduleLoadingState';
import { useEventContext } from '../../../contexts/EventContext';
import { useAgendas, useAgenda } from '../../../../hooks/useAgendas';
import { useEventDetails } from '../../../../hooks/useEventDetails';
import type { ScheduleSession } from '../../../../types/schedule.types';
import { transformAgendaToScheduleData, generateCalendarUrl } from '../../../components/schedule/utils/dataTransformers';
import { getDefaultScheduleDay } from '../../../../utils/scheduleHelpers';

/**
 * V1 Event Schedule Page - FROZEN
 *
 * This component displays the schedule/agenda for V1 events only.
 * It is a simplified version without V2 features like:
 * - Session type filtering
 * - Materials modal
 * - Speaker modal (just shows names)
 * - Sponsor sections
 * - Multiple file downloads
 *
 * DO NOT add V2 features to this component.
 * This component will be removed after V1 sunset.
 */
export const SchedulePageV1 = () => {
  const { eventSlug, style } = useEventContext();
  const primaryColor = style?.headerColor || '#2563eb'; // Default to blue if no color provided
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedDayId, setSelectedDayId] = useState<string | undefined>();
  const [selectedSession, setSelectedSession] = useState<ScheduleSession | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewType, setViewType] = useState<'list'>('list');
  const [showMaterialsTab, setShowMaterialsTab] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scheduleTopRef = useRef<HTMLDivElement>(null);

  // Fetch event details for timezone
  const { data: eventDetails } = useEventDetails(eventSlug);

  // Update page title
  useEffect(() => {
    if (eventDetails) {
      document.title = `Schedule - ${eventDetails.name}`;
    }
  }, [eventDetails]);

  // Handle scroll to show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling down 300px
      const scrolled = window.scrollY > 300;
      setShowScrollToTop(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch agendas list
  const { data: agendas, isLoading: agendasLoading, error: agendasError } = useAgendas(eventSlug);

  // Get the virtual V1 agenda (there's always exactly one)
  const v1Agenda = agendas?.[0];

  // Fetch full agenda with days and sessions
  const { data: agendaData, isLoading: agendaLoading, error: agendaError } = useAgenda(
    eventSlug,
    v1Agenda?.id || null
  );

  // Transform agenda data to component format
  const scheduleData = agendaData ? transformAgendaToScheduleData(agendaData) : [];

  // Set default day when schedule data is loaded
  useEffect(() => {
    if (scheduleData.length > 0 && !selectedDay) {
      // Use helper to find the optimal default day
      const defaultDay = getDefaultScheduleDay(scheduleData, eventDetails?.timeZone);

      if (defaultDay) {
        setSelectedDay(defaultDay.date);
        setSelectedDayId(defaultDay.id);
      }
    }
  }, [scheduleData, selectedDay, eventDetails?.timeZone]);


  const isLoading = agendasLoading || agendaLoading;
  const hasError = agendasError || agendaError;

  // Timezone information
  const eventTimeZone = eventDetails?.timeZone || 'Pacific Time (US & Canada)';

  // Loading state
  if (isLoading) {
    return (
      <div className={`min-h-[calc(100vh-64px)]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <ScheduleLoadingState />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className={`min-h-[calc(100vh-64px)]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2">Unable to Load Schedule</h2>
            <p className="text-gray-600">There was an error loading the event schedule. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  // No agenda state
  if (!agendas || agendas.length === 0) {
    return (
      <div className={`min-h-[calc(100vh-64px)]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-gray-400 mb-4">
              <Calendar className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Schedule Coming Soon</h2>
            <p className="text-gray-600">The event schedule is not available yet. Please check back later.</p>
          </div>
        </div>
      </div>
    );
  }

  // No days/sessions state
  if (scheduleData.length === 0) {
    return (
      <div className={`min-h-[calc(100vh-64px)]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-gray-400 mb-4">
              <Calendar className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No Sessions Scheduled</h2>
            <p className="text-gray-600">There are no sessions scheduled for this event yet.</p>
          </div>
        </div>
      </div>
    );
  }

  const currentDay = selectedDayId
    ? scheduleData.find(day => day.id === selectedDayId)
    : scheduleData.find(day => day.date === selectedDay) || scheduleData[0];

  // Group sessions by time - V1 only has search filtering
  const groupedSessions = currentDay.sessions
    .filter(session => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      return session.title.toLowerCase().includes(searchLower) ||
             session.description.toLowerCase().includes(searchLower) ||
             session.speakers.some(speaker =>
               speaker.name.toLowerCase().includes(searchLower)
             );
    })
    .reduce((acc, session) => {
      const timeSlot = acc.find(slot => slot.time === session.time);
      if (timeSlot) {
        timeSlot.sessions.push(session);
      } else {
        acc.push({
          time: session.time,
          endTime: session.endTime,
          sessions: [session]
        });
      }
      return acc;
    }, [] as Array<{
      time: string;
      endTime: string;
      sessions: ScheduleSession[];
    }>);

  // Download schedule if available
  const downloadSchedule = () => {
    const attachments = agendaData?.showableAttachments || agendaData?.attachments;
    if (attachments && attachments.length > 0) {
      const firstAttachment = attachments[0];
      const downloadUrl =  firstAttachment.url || firstAttachment.downloadUrl || firstAttachment.fileUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
    }
  };

  const hasDownloadableSchedule = (agendaData?.showableAttachments?.length || 0) > 0 ||
                                  (agendaData?.attachments?.length || 0) > 0;

  // Get agenda options
  const agendaOptions = agendaData?.agendaOption;

  // Calculate total sessions for current day
  const totalSessions = currentDay?.sessions?.length || 0;

  // Scroll to top handler with offset for navbar
  const scrollToTop = () => {
    if (scheduleTopRef.current) {
      const yOffset = -80; // Offset for navbar height
      const element = scheduleTopRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  console.log('SchedulePageV1 - Rendered with: totalSessions:', totalSessions, 'selectedDay:', selectedDay, 'selectedDayId:', selectedDayId);

  return (
    <div className={`min-h-[calc(100vh-64px)]`}>
      {/* Scroll anchor */}
      <div ref={scheduleTopRef} />

      {/* Main container with backdrop blur and semi-transparent background */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white backdrop-blur-sm shadow-xl rounded-xl border border-gray-200/50">
          <div className="p-6 sm:p-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Schedule</h1>
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                  <Clock size={16} />
                  <span>All times shown in {eventTimeZone}</span>
                </div>
              </div>
              {hasDownloadableSchedule && (
                <button
                  onClick={downloadSchedule}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-all hover:shadow-lg"
                  style={{
                    backgroundColor: primaryColor,
                    filter: 'brightness(1)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
                >
                  <Download size={16} />
                  <span>Download Schedule</span>
                </button>
              )}
            </div>

            {/* Agenda Description */}
            {agendaOptions?.agendaDescription && (
              <div className="prose prose-blue max-w-none">
                <div dangerouslySetInnerHTML={{ __html: agendaOptions.agendaDescription }} />
              </div>
            )}

            {/* Search and View Toggle */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Search className="text-gray-400" size={20} />
                </div>
                <input
                  type="text"
                  placeholder="Search sessions or speakers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 h-10 border rounded-lg focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                />
              </div>
            </div>

            {/* Day Tabs */}
            <DayTabs
              days={scheduleData}
              selectedDay={selectedDay}
              onDayChange={setSelectedDay}
              primaryColor={primaryColor}
              selectedDayId={selectedDayId}
              onDayChangeById={(dayId, date) => {
                setSelectedDayId(dayId);
                setSelectedDay(date);
              }}
            />

            {/* Sessions */}
            <div>
              {groupedSessions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <Search size={48} className="mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
                  <p className="text-gray-600">
                    Try adjusting your search criteria
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {groupedSessions.map((timeSlot, index) => (
                    <TimeSlot
                      key={`${timeSlot.time}-${index}`}
                      time={timeSlot.time}
                      endTime={timeSlot.endTime}
                      sessions={timeSlot.sessions}
                      onSessionClick={(session) => {
                        setSelectedSession(session);
                        setShowMaterialsTab(false);
                      }}
                      onMaterialsClick={(session) => {
                        setSelectedSession(session);
                        setShowMaterialsTab(true);
                      }}
                      viewType={viewType}
                      agendaOptions={agendaOptions}
                      primaryColor={primaryColor}
                      eventDetails={eventDetails}
                      eventDate={currentDay?.date}
                      eventTimeZone={eventDetails?.timeZone}
                      ExpandedSessionCard={ExpandedSessionCardV1}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Scroll to Top Button - Show when scrolled and more than 5 sessions */}
      {totalSessions > 5 && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 bg-white hover:bg-gray-50 text-gray-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 border border-gray-200 ${
            showScrollToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
          }`}
          aria-label="Scroll to top"
        >
          <ChevronUp size={24} />
        </button>
      )}

      {/* Session Modal - V1 version without speaker modal support */}
      {selectedSession && (
        <SessionModal
          session={selectedSession}
          onClose={() => {
            setSelectedSession(null);
            setShowMaterialsTab(false);
          }}
          timeZoneLabel={eventTimeZone}
          onCalendarAdd={(type) => {
            const url = generateCalendarUrl(selectedSession, type, selectedDay);
            if (type === 'apple') {
              const link = document.createElement('a');
              link.href = url;
              link.download = 'event.ics';
              link.click();
            } else {
              window.open(url, '_blank');
            }
          }}
          ModalWrapper={PortalModal}
          // V1 doesn't have speaker modal
          SpeakerModal={undefined}
          speakers={[]}
          initialTab={showMaterialsTab ? 'attachments' : 'details'}
        />
      )}
    </div>
  );
};